import hvpAuthenticatedApi from "../api/hvpAuthenticatedApi";
import { convertDatesToDayJS, convertDatesToString } from "../shared/helpers";
import {
  ApiResponseCreate,
  ApiResponseDelete,
  ApiResponseList,
  ApiResponseOne,
  ApiResponseUpdate,
  AxiosParams,
  BaseFormValues,
  BaseRequest,
  BaseResponse,
} from "../shared/types";
import { HandleErrorsService } from "./handle-error.service";

export abstract class BaseService<
  REQ extends BaseRequest,
  RES extends BaseResponse,
  FV extends BaseFormValues
> {
  constructor(private readonly API_URL: string) {
    if (API_URL.endsWith("/")) {
      throw new Error("API_URL should not end with a slash");
    }
  }

  private handleServiceError(error: any): never {
    const parsedError = HandleErrorsService.parse(error);
    throw parsedError;
  }

  private async handleRequest<T>(request: () => Promise<T>): Promise<T> {
    try {
      return await request();
    } catch (error) {
      this.handleServiceError(error);
    }
  }

  async getAll(params?: AxiosParams): Promise<ApiResponseList<RES>> {
    return this.handleRequest(() =>
      hvpAuthenticatedApi
        .get<ApiResponseList<RES>>(this.API_URL, { params })
        .then((r) => r.data)
    );
  }

  async getById(id: string): Promise<ApiResponseOne<RES>> {
    return this.handleRequest(() =>
      hvpAuthenticatedApi
        .get<ApiResponseOne<RES>>(`${this.API_URL}/${id}`)
        .then((r) => r.data)
    );
  }

  async create(req: REQ): Promise<ApiResponseCreate<RES>> {
    try {
      const response = await hvpAuthenticatedApi.post<ApiResponseCreate<RES>>(
        this.API_URL,
        req
      );
      return response.data;
    } catch (error) {
      this.handleServiceError(error);
    }
  }

  async update(id: string, req: REQ): Promise<ApiResponseUpdate<RES>> {
    try {
      const response = await hvpAuthenticatedApi.patch<ApiResponseUpdate<RES>>(
        `${this.API_URL}/${id}`,
        req
      );
      return response.data;
    } catch (error) {
      this.handleServiceError(error);
    }
  }

  async delete(id: string): Promise<ApiResponseDelete> {
    try {
      const response = await hvpAuthenticatedApi.delete<ApiResponseDelete>(
        `${this.API_URL}/${id}`
      );
      return response.data;
    } catch (error) {
      this.handleServiceError(error);
    }
  }

  public transformResponseToFormValues(response: RES): FV {
    return convertDatesToDayJS(response) as unknown as FV;
  }

  public transformFormValuesToRequest(formValues: FV): REQ {
    return convertDatesToString(formValues) as unknown as REQ;
  }
}
